import type { QuickRebuttal } from "@expert/contentful";

/**
 * Formats a QuickRebuttal object into Markdown with numbered objections
 * and bullet-pointed responses.
 *
 * Example:
 * ```ts
 * const doc: QuickRebuttal = {
 *   objections: [
 *     {
 *       id: "obj-1",
 *       title: "Too Expensive / Budget",
 *       responses: [
 *         { id: "resp-1", text: "I totally respect that you’re mindful of your budget…" }
 *       ]
 *     },
 *     {
 *       id: "obj-2",
 *       title: "Need Time to Think",
 *       responses: [
 *         { id: "resp-2", text: "Absolutely, I understand wanting to think it through…" }
 *       ]
 *     }
 *   ]
 * };
 *
 * console.log(formatQuickRebuttal(doc));
 *
 * // Output:
 * // 1. “Too Expensive / Budget”
 * // - “I totally respect that you’re mindful of your budget…”
 * //
 * // 2. “Need Time to Think”
 * // - “Absolutely, I understand wanting to think it through…”
 * ```
 *
 * @param doc The QuickRebuttal object containing objections and responses.
 * @returns A Markdown-formatted string.
 */
export function formatQuickRebuttal(doc?: QuickRebuttal): string {
    if (!doc?.objections?.length) return "";

    //TODO: If you are refactoring this function, look at refactoring the Contentful side to handle all the formatting
    return doc.objections
        .map((objection, i) => {
            const { title, responses = [] } = objection;

            const outerListItem = `${i + 1}. ${title === "Early Rebuttal" ? title : q(title)}`;
            const innerListItems = responses.map((response) => `- ${q(response.text)}`).join("\n");

            return innerListItems ? `${outerListItem}\n${innerListItems}` : outerListItem;
        })
        .join("\n\n");
}

const q = (s = "") => `“${s}”`;
